import _ from 'lodash';
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	pointerWithin,
	useDndContext,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { DeleteIcon, ReorderIcon } from '@treams/treams-icons';
import { Button, Library, Skeleton, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import FeedbackTemplateQuestion from './FeedbackTemplateQuestion';
import AddFeedbackQuestion from './AddFeedbackQuestion';
import FeedbackQuestionCard from './FeedbackQuestionCard';

function FeedbackTemplateQuestionsDraggable(props: {
	loading: string[];
	questions: graphql.FeedbackQuestion[];
	onAddQuestions: (questions: graphql.FeedbackQuestion[]) => void;
	onDeleteQuestion: (question: graphql.FeedbackQuestion) => Promise<void>;
}) {
	const { t } = useTranslation();

	const { active } = useDndContext();

	return (
		<SortableContext items={props.questions.map((item) => `item:${item.id}`)} strategy={() => null}>
			<Subheading mb={3}>{t('settings.feedbackLibrary.template.questions.title')}</Subheading>
			{props.questions.map((question, i) => (
				<Box key={question.id} mb={2}>
					<FeedbackTemplateQuestion
						index={i}
						question={question}
						loading={props.loading.includes(question.id)}
						onDelete={() => props.onDeleteQuestion(question)}
					/>
				</Box>
			))}
			<Box mb={2}>
				<AddFeedbackQuestion onSelect={props.onAddQuestions} />
			</Box>
			<DragOverlay>
				{active && (
					<Library.Item
						handleIcon={<ReorderIcon />}
						dragging
						active
						item={props.questions.find(({ id }) => `item:${id}` === active.id)!}
						buttons={[
							// This one probably has the wrong type in Library.Item.Draggable
							// eslint-disable-next-line react/jsx-key
							<Button
								type="destructive"
								title={t('settings.feedbackLibrary.template.questions.remove.button')}
								ml={2}
								small
								icon={<DeleteIcon />}
							/>,
						]}
						renderCard={(props) => <FeedbackQuestionCard {...props} />}
					/>
				)}
			</DragOverlay>
		</SortableContext>
	);
}

export default function FeedbackTemplateQuestions(props: {
	questions: graphql.FeedbackQuestion[];
	onChange: (questions: graphql.FeedbackQuestion[]) => Promise<void>;
}) {
	const [questions, setQuestions] = useState(props.questions as graphql.FeedbackQuestion[]);
	const [loading, setLoading] = useState<Record<string, boolean>>({});

	const sensors = useSensors(useSensor(PointerSensor));

	return (
		<DndContext
			collisionDetection={pointerWithin}
			modifiers={[restrictToVerticalAxis]}
			sensors={sensors}
			onDragOver={(event) => {
				setQuestions((questions) => {
					if (typeof event.active.id !== 'string' || typeof event.over?.id !== 'string') {
						return questions;
					}

					const source = questions.findIndex(({ id }) => `item:${id}` === event.active.id);
					const destination = questions.findIndex(({ id }) => `item:${id}` === event.over?.id);

					return arrayMove(questions, source, destination);
				});
			}}
			onDragEnd={async (event) => {
				const id = event.active.id.toString().substring('item:'.length);
				setLoading((loading) => ({ ...loading, [id]: true }));
				await props.onChange(questions);
				setLoading((loading) => _.omit(loading, id));
			}}
		>
			<FeedbackTemplateQuestionsDraggable
				loading={Object.keys(loading)}
				questions={questions}
				onAddQuestions={(questions) => {
					setQuestions((existing) => {
						const result = _.uniqBy(existing.concat(questions), 'id');
						(async () => {
							for (const { id } of questions) {
								setLoading((loading) => ({ ...loading, [id]: true }));
							}
							await props.onChange(result);
							setLoading((loading) =>
								_.omit(
									loading,
									questions.map(({ id }) => id),
								),
							);
						})();
						return result;
					});
				}}
				onDeleteQuestion={async (question) => {
					const result = questions.filter(({ id }) => id !== question.id);
					await props.onChange(result);
					setQuestions(result);
				}}
			/>
		</DndContext>
	);
}

function FeedbackTemplateQuestionsSkeleton() {
	const { t } = useTranslation();

	return (
		<Box>
			<Subheading mb={3}>{t('settings.feedbackLibrary.template.questions.title')}</Subheading>
			<Skeleton.Repeat count={3}>
				<Box mb={2}>
					<FeedbackTemplateQuestion.Skeleton />
				</Box>
			</Skeleton.Repeat>
		</Box>
	);
}

FeedbackTemplateQuestions.Skeleton = FeedbackTemplateQuestionsSkeleton;
